import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { CanalEntrega, FichaPlantilla, ListaPlantillas, Plantilla, VistaPrevia } from '../../api/ops/entregas';
import { versionEntregas } from '../../api/ops/entregas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla } from '../../componentes/Tabla';
import { rolesDe } from '../../logica/acceso';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { CANALES, PERMISOS_MENSAJES, chipMeta, errorDeHuecos, nombreIdioma, nombreVariable, trozosDe } from './reglas';
import { AYUDA_PLANTILLAS } from './textos';

/**
 * «Mensajes a clientes» (5.3): una fila por mensaje, canal e idioma de los módulos activos (`listDeliveryTemplates`),
 * «De Reservaria» o «Con tu texto» y el chip de Meta solo para «En revisión», «Rechazada» y «En pausa». Filtro por canal
 * sin cifras (G140). La ficha enseña el texto de Reservaria con los datos que se rellenan solos y **solo deja escribir los
 * huecos de texto libre** (G50, con su límite y los datos que admite cada uno, `allowed_variables`, G301), con la vista
 * previa de la API (`previewDeliveryTemplate`). La primera vez personaliza (`customizeDeliveryTemplate`); luego, versión
 * nueva con `If-Match` (`createDeliveryTemplateVersion`). «Volver al texto de Reservaria» con «Deshacer» (no envía nada).
 * En WhatsApp e Instagram, mientras Meta revisa la versión nueva sigue saliendo la última aprobada.
 */
const FILTROS: readonly { etiqueta: string; canal: CanalEntrega | null }[] = [
    { etiqueta: 'Todos', canal: null },
    { etiqueta: 'WhatsApp', canal: 'whatsapp' },
    { etiqueta: 'Instagram', canal: 'instagram' },
    { etiqueta: 'Email', canal: 'email' },
    // Telegram solo se ofrece si el negocio ya tiene mensajes por ahí (canal conectado).
    { etiqueta: 'Telegram', canal: 'telegram' },
];

const claveDe = (p: Pick<Plantilla, 'message_type' | 'channel' | 'locale'>) => `${p.message_type}|${p.channel}|${p.locale}`;

export function Plantillas(props: PropsPantalla) {
    const { ops, negocio, movil, membresia, nombreNegocio, navegar, busqueda } = props;
    const permisos = usePermisos();
    const puede = permisos.puede(PERMISOS_MENSAJES.plantillas);
    const [filtro, setFiltro] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaPlantillas | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [hayTelegram, setHayTelegram] = useState(false);
    const [abierta, setAbierta] = useState<string | null>(null);
    const pedido = busqueda.get('mensaje');

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const canal = FILTROS[filtro]?.canal ?? null;
    const vista = `${canal ?? ''}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.listDeliveryTemplates(negocio, { ...(canal === null ? {} : { channel: canal }), cursor: pag.cursor ?? undefined })
            .then((l) => {
                if (vivo) {
                    if (l.data.some((p) => p.channel === 'telegram')) {
                        setHayTelegram(true);
                    }
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada({ vista, lista: null, error: e });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, canal, vista, pag.cursor]);

    useCabecera({ ayuda: puede ? AYUDA_PLANTILLAS : [], acciones: [] });

    if (!puede) {
        return <EstadoSinPermiso seccion="Mensajes a clientes" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const actual = cargada?.vista === vista ? cargada : null;
    if (actual?.error != null && actual.lista === null) {
        return <EstadoError seccion="los mensajes a clientes" codigoSoporte={estadoDeError(actual.error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    const lista = actual?.lista ?? null;
    // `?mensaje=` (desde «Cambiar el texto» de un recordatorio) abre el primero de ese tipo de mensaje.
    const elegida = lista?.data.find((p) => claveDe(p) === abierta) ?? (abierta === null && pedido !== null ? lista?.data.find((p) => p.message_type === pedido) : undefined) ?? null;

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Canal" items={hayTelegram ? FILTROS : FILTROS.filter((f) => f.canal !== 'telegram')} valor={filtro} onCambio={(i) => { setFiltro(i); reiniciar(); }} />
            {lista === null ? (
                <EstadoCargando seccion="los mensajes a clientes" />
            ) : (
                <Tabla
                    etiqueta="Mensajes a clientes"
                    movil={movil}
                    columnas={[
                        { clave: 'mensaje', titulo: 'Mensaje' },
                        { clave: 'canal', titulo: 'Canal' },
                        { clave: 'idioma', titulo: 'Idioma' },
                        { clave: 'texto', titulo: 'Texto' },
                    ]}
                    filas={lista.data.map((p) => {
                        const chip = chipMeta(p);
                        return {
                            clave: claveDe(p),
                            etiqueta: p.title ?? p.message_type,
                            onAbrir: () => { setAbierta(claveDe(p)); },
                            celdas: {
                                mensaje: (
                                    <span className="rv-en-celda">
                                        <strong>{p.title ?? p.message_type}</strong>
                                        {p.module !== undefined && <small>{p.module.charAt(0).toLocaleUpperCase('es') + p.module.slice(1)}</small>}
                                    </span>
                                ),
                                canal: CANALES[p.channel],
                                idioma: nombreIdioma(p.locale),
                                texto: (
                                    <span className="rv-en-chip-fila">
                                        {p.customized ? 'Con tu texto' : 'De Reservaria'}
                                        {chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}
                                    </span>
                                ),
                            },
                        };
                    })}
                    vacia={
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa"><Icono nombre="message-square-text" /></span>
                            <strong>No hay mensajes aquí</strong>
                            <span>{canal === null ? 'Los mensajes los declaran los módulos que tienes activos.' : 'Prueba con otro canal.'}</span>
                        </div>
                    }
                />
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            )}
            {elegida !== null && (
                <FichaPlantillaPanel
                    key={claveDe(elegida)}
                    {...props}
                    plantilla={elegida}
                    onCerrar={() => {
                        setAbierta('');
                    }}
                    onCambiada={recargar}
                />
            )}
        </div>
    );
}

function FichaPlantillaPanel({ ops, negocio, movil, soloLectura, nombreNegocio, plantilla: p, onCerrar, onCambiada }: PropsPantalla & { plantilla: Plantilla; onCerrar: () => void; onCambiada: () => void }) {
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [pestana, setPestana] = useState(0);
    const [ficha, setFicha] = useState<ConVersion<FichaPlantilla> | null>(null);
    const [valores, setValores] = useState<Record<string, string>>({});
    const [previa, setPrevia] = useState<VistaPrevia | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const foco = useRef<{ clave: string; pos: number } | null>(null);
    const huecos = p.editable_slots;
    const meta = p.channel === 'whatsapp' || p.channel === 'instagram';

    useEffect(() => {
        if (!p.customized || p.template_id == null) {
            return;
        }
        let vivo = true;
        ops.getDeliveryTemplate(negocio, p.template_id)
            .then((f) => {
                if (!vivo) {
                    return;
                }
                setFicha(f);
                const vigente = f.datos.versions.find((v) => v.id === f.datos.current_version_id) ?? f.datos.versions[f.datos.versions.length - 1];
                if (vigente !== undefined) {
                    setValores({ ...vigente.slot_values });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, p.customized, p.template_id]);

    // Vista previa de la API con datos de ejemplo, un momento después de dejar de escribir.
    const firma = JSON.stringify(valores);
    useEffect(() => {
        let vivo = true;
        const reloj = setTimeout(() => {
            ops.previewDeliveryTemplate(negocio, { message_type: p.message_type, channel: p.channel, locale: p.locale, slot_values: JSON.parse(firma) as Record<string, string> })
                .then((v) => {
                    if (vivo) {
                        setPrevia(v);
                    }
                })
                .catch(() => undefined);
        }, 400);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, p.message_type, p.channel, p.locale, firma]);

    const version = ficha?.version ?? (ficha === null ? null : versionEntregas(ficha.datos.version));
    const guardar = () => {
        const fallo = errorDeHuecos(huecos, valores);
        if (fallo !== null) {
            setError(fallo);
            return;
        }
        setError(null);
        setOcupado(true);
        const peticion =
            p.customized && p.template_id != null
                ? ops.createDeliveryTemplateVersion(negocio, p.template_id, valores, version ?? '0')
                : ops.customizeDeliveryTemplate(negocio, { message_type: p.message_type, channel: p.channel, locale: p.locale, slot_values: valores });
        peticion
            .then(() => {
                avisar({ tipo: 'ok', texto: meta ? 'Guardado y enviado a Meta. Mientras lo revisa, sale el texto aprobado.' : 'Texto guardado. Sale así desde ahora.' });
                onCambiada();
                onCerrar();
            })
            .catch((e: unknown) => {
                const breve = avisoBreveDeError(e);
                setError(breve ?? (e instanceof ErrorApi && (e.estado === 412 || e.estado === 409) ? 'Otra persona lo ha cambiado. Ciérralo y vuelve a abrirlo.' : e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido guardar el texto. Prueba de nuevo.'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const volverAReservaria = () => {
        if (p.template_id == null) {
            return;
        }
        const id = p.template_id;
        envio.programar({
            texto: 'Vuelve el texto de Reservaria.',
            donde: 'ficha',
            enviar: () => {
                ops.resetDeliveryTemplate(negocio, id, version ?? '0')
                    .then(onCambiada)
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No he podido volver al texto de Reservaria. Prueba de nuevo.' });
                    });
            },
        });
    };

    const insertar = (clave: string, variable: string) => {
        const actual = valores[clave] ?? '';
        const pos = foco.current?.clave === clave ? foco.current.pos : actual.length;
        const marca = variable.startsWith('{{') ? variable : `{{${variable}}}`;
        setValores({ ...valores, [clave]: `${actual.slice(0, pos)}${marca}${actual.slice(pos)}` });
    };

    const chip = chipMeta(p);
    const versiones = ficha?.datos.versions ?? [];
    const vigente = versiones.find((v) => v.in_use === true);
    const ultima = versiones[versiones.length - 1];

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={p.title ?? p.message_type}
            eyebrow={`Mensaje · ${CANALES[p.channel]} · ${nombreIdioma(p.locale)}`}
            icono={p.channel === 'email' ? 'mail' : p.channel === 'instagram' ? 'instagram' : 'message-circle'}
            chip={chip?.[0]}
            chipEstado={chip?.[1]}
            accion={soloLectura || huecos.length === 0 ? '' : meta ? 'Guardar y enviar a Meta' : 'Guardar'}
            accionIcono={meta ? 'send' : 'check'}
            ocupado={ocupado}
            ocupadoTexto="Guardando…"
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo">
                <Filtros etiqueta="Qué ver" items={[{ etiqueta: 'Texto' }, { etiqueta: 'Versiones' }]} valor={pestana} onCambio={setPestana} />
                {envio.pendiente !== null && (
                    <div className="rv-ca-hecho" role="status">
                        <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                    </div>
                )}
                {p.provider_status === 'pending' && ultima !== undefined && (
                    <div className="rv-ca-caja rv-ca-caja--aviso"><span className="rv-ca-caja__fila"><Icono nombre="hourglass" />{`La versión ${String(ultima.number)} está en revisión de Meta. Mientras, sale ${vigente === undefined ? 'el texto aprobado' : `la versión ${String(vigente.number)}`}.`}</span></div>
                )}
                {(p.provider_status === 'rejected' || p.provider_status === 'paused') && (
                    <div className="rv-ca-caja rv-ca-caja--error">
                        <strong>{p.provider_status === 'rejected' ? 'Meta ha rechazado tu texto' : 'Meta ha pausado esta plantilla'}</strong>
                        {ultima?.provider_reason != null && <span>{ultima.provider_reason}</span>}
                        <span>Mientras, sale el último texto aprobado.</span>
                    </div>
                )}
                {pestana === 0 ? (
                    <>
                        <div className="rv-ca-bloque">
                            <div className="rv-ca-bloque__cab"><strong>Texto de Reservaria</strong></div>
                            {p.base_subject != null && <div className="rv-ca-dato"><span>Asunto</span><strong>{p.base_subject}</strong></div>}
                            <div className="rv-en-base">
                                {trozosDe(p.base_body ?? '').map((t, i) =>
                                    t.tipo === 'texto' ? <span key={i}>{t.texto}</span> : t.tipo === 'variable' ? <span key={i} className="rv-en-dato-auto">{nombreVariable(t.nombre)}</span> : <span key={i} className="rv-en-hueco">{`[${huecos.find((h) => h.key === t.clave)?.label ?? 'Texto libre'}]`}</span>,
                                )}
                            </div>
                            <p className="rv-ca-nota rv-ca-nota--gris">{huecos.length === 0 ? 'Este mensaje no tiene huecos para tu texto.' : 'Lo gris no se cambia. Tú escribes lo que va en los huecos.'}</p>
                        </div>
                        {huecos.map((h) => (
                            <div key={h.key} className="rv-ca-bloque">
                                <Campo
                                    etiqueta={h.label}
                                    valor={valores[h.key] ?? ''}
                                    filas={3}
                                    max={h.max_length}
                                    bloqueado={soloLectura}
                                    onChange={(v) => {
                                        setValores({ ...valores, [h.key]: v });
                                        foco.current = { clave: h.key, pos: v.length };
                                    }}
                                />
                                <span className="rv-en-contador">{`${String((valores[h.key] ?? '').length)} de ${String(h.max_length)} caracteres`}</span>
                                {!soloLectura && (h.allowed_variables ?? []).length > 0 && (
                                    <div className="rv-en-insertar">
                                        <span>Insertar</span>
                                        {(h.allowed_variables ?? []).map((v) => (
                                            <button key={v} type="button" onClick={() => { insertar(h.key, v); }}>
                                                {`{ } ${nombreVariable(v)}`}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {error !== null && <p className="rv-ca-nota" style={{ color: "var(--rv-red)" }} role="alert"><Icono nombre="circle-alert" /><span>{error}</span></p>}
                        <div className="rv-ca-bloque">
                            <div className="rv-ca-bloque__cab"><strong>Vista previa</strong><span>Con datos de ejemplo</span></div>
                            <div className="rv-en-previa">
                                <strong>{nombreNegocio}</strong>
                                {previa === null ? (
                                    <span className="rv-ca-nota rv-ca-nota--gris">Preparando la vista previa…</span>
                                ) : (
                                    <>
                                        {previa.subject != null && <span className="rv-en-previa__asunto">{previa.subject}</span>}
                                        <div className="rv-en-previa__burbuja">{previa.body}</div>
                                        {(previa.warnings ?? []).map((w) => (
                                            <span key={w} className="rv-ca-nota"><Icono nombre="info" /><span>{w}</span></span>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                        {p.customized && !soloLectura && (
                            <Boton tipo="secundario" icono="rotate-ccw" movil={movil} onClick={volverAReservaria}>
                                Volver al texto de Reservaria
                            </Boton>
                        )}
                    </>
                ) : (
                    <div className="rv-ca-bloque">
                        {!p.customized ? (
                            <p className="rv-ca-nota rv-ca-nota--gris">Sale el texto de Reservaria. Cuando escribas el tuyo, verás aquí sus versiones.</p>
                        ) : ficha === null ? (
                            <EstadoCargando seccion="las versiones" />
                        ) : (
                            [...versiones].reverse().map((v) => {
                                const c = chipMeta(v);
                                return (
                                    <div key={v.id} className="rv-ca-dato">
                                        <span className="rv-en-chip-fila">
                                            {`Versión ${String(v.number)} · ${diaYHora(v.created_at)}`}
                                            {v.in_use === true && <Chip estado="confirmed">En uso</Chip>}
                                            {c !== null && <Chip estado={c[1]}>{c[0]}</Chip>}
                                        </span>
                                        {v.provider_reason != null && <strong>{v.provider_reason}</strong>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}
